import { useState, useCallback } from 'react';
import api, { resolveMediaUrl } from '../services/api';
import { toast } from '../utils/toastBus';

const DEFAULT_ASSET_LIMIT = 50;
const DEFAULT_VIDEO_LIMIT = 50;

export function useAssetManagement(projectId) {
  const [assets, setAssets] = useState({ images: [], audio: [], videos: [], music: [] });
  const [archived, setArchived] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    audio: { total: 0, offset: 0, limit: DEFAULT_ASSET_LIMIT },
    images: { total: 0, offset: 0, limit: DEFAULT_ASSET_LIMIT },
    videos: { total: 0, offset: 0, limit: DEFAULT_VIDEO_LIMIT },
    music: { total: 0, offset: 0, limit: DEFAULT_ASSET_LIMIT }
  });

  const normalizeAsset = useCallback((item) => {
    if (!item || typeof item !== 'object') return item;
    
    const url = resolveMediaUrl(item.url || item.path || item.audioPath || '');
    const thumbnailUrl = item.thumbnailUrl ? resolveMediaUrl(item.thumbnailUrl) : null;
    const waveformUrl = item.waveformUrl ? resolveMediaUrl(item.waveformUrl) : null;
    const durationMs = Number.isFinite(Number(item.durationMs)) 
      ? Number(item.durationMs) 
      : (Number.isFinite(Number(item.duration_ms)) ? Number(item.duration_ms) : null);
    
    const previewStatus = typeof item.previewStatus === 'string' && item.previewStatus.trim()
      ? item.previewStatus.trim()
      : (typeof item.preview_status === 'string' && item.preview_status.trim() ? item.preview_status.trim() : null);
    
    const previewGeneratedAt = typeof item.previewGeneratedAt === 'string' && item.previewGeneratedAt.trim()
      ? item.previewGeneratedAt.trim()
      : (typeof item.preview_generated_at === 'string' && item.preview_generated_at.trim() ? item.preview_generated_at.trim() : null);

    return {
      ...item,
      url,
      thumbnailUrl,
      waveformUrl,
      durationMs,
      previewStatus,
      previewGeneratedAt
    };
  }, []);

  const mergeVideoLists = useCallback((prevList, nextList) => {
    if (!Array.isArray(prevList)) return Array.isArray(nextList) ? nextList : [];
    if (!Array.isArray(nextList) || !nextList.length) return prevList;
    const seen = new Set();
    const result = [];
    const pushUnique = (video) => {
      if (!video) return;
      const key = String(video.id || video.assetId || video._id || video.url || Math.random());
      if (seen.has(key)) return;
      seen.add(key);
      result.push(video);
    };
    [...prevList, ...nextList].forEach(pushUnique);
    return result;
  }, []);

  const loadAssets = useCallback(async (options = {}) => {
    const {
      offset = 0,
      videosOffset = 0,
      appendVideos = false,
      suppressLoading = false
    } = options;
    if (!projectId) return;
    
    if (!suppressLoading) setLoading(true);
    try {
      const [assetsRes, archivedRes] = await Promise.all([
        (async () => {
          try {
            const query = new URLSearchParams({
              limit: String(DEFAULT_ASSET_LIMIT),
              videosLimit: String(DEFAULT_VIDEO_LIMIT),
              offset: String(offset),
              videosOffset: String(videosOffset)
            });
            const j = await api.get(`/api/composer/projects/${encodeURIComponent(String(projectId))}/assets?${query.toString()}`);
            if (j && j.error) {
              try {
                toast({ type: 'error', message: `Assets unavailable: ${j.error}` });
              } catch {}
            } else if (j && j.ok === false) {
              try {
                toast({ type: 'error', message: 'Assets unavailable (permissions)' });
              } catch {}
            }
            return j;
          } catch (e) {
            try {
              toast({ type: 'error', message: 'Assets failed to load' });
            } catch {}
            return {};
          }
        })(),
        (async () => {
          try {
            return await api.get(`/api/composer/projects/${encodeURIComponent(String(projectId))}/archives`);
          } catch {
            return {};
          }
        })()
      ]);

      const normalizedImages = Array.isArray(assetsRes?.items?.images) ? assetsRes.items.images.map(normalizeAsset) : [];
      const normalizedAudio = Array.isArray(assetsRes?.items?.audio) ? assetsRes.items.audio.map(normalizeAsset) : [];
      const normalizedVideos = Array.isArray(assetsRes?.items?.videos) ? assetsRes.items.videos.map(normalizeAsset) : [];
      const normalizedMusic = Array.isArray(assetsRes?.items?.music)
        ? assetsRes.items.music.map((mus) => ({ ...mus, url: resolveMediaUrl(mus?.url || mus?.path || '') }))
        : [];

      setAssets((prev) => ({
        images: appendVideos ? prev.images : normalizedImages,
        audio: appendVideos ? prev.audio : normalizedAudio,
        music: appendVideos ? prev.music : normalizedMusic,
        videos: appendVideos ? mergeVideoLists(prev.videos, normalizedVideos) : normalizedVideos,
      }));

      setPagination({
        audio: assetsRes?.pagination?.audio || { total: normalizedAudio.length, offset, limit: DEFAULT_ASSET_LIMIT },
        images: assetsRes?.pagination?.images || { total: normalizedImages.length, offset, limit: DEFAULT_ASSET_LIMIT },
        videos: assetsRes?.pagination?.videos || { total: normalizedVideos.length, offset: videosOffset, limit: DEFAULT_VIDEO_LIMIT },
        music: assetsRes?.pagination?.music || { total: normalizedMusic.length, offset, limit: DEFAULT_ASSET_LIMIT }
      });

      setArchived(Array.isArray(archivedRes?.items) ? archivedRes.items.map(normalizeAsset) : []);
    } finally {
      if (!suppressLoading) setLoading(false);
    }
  }, [projectId, normalizeAsset, mergeVideoLists]);

  const archiveAsset = useCallback(async (asset) => {
    if (!asset) return;
    const assetId = asset.id || asset.assetId || asset._id;
    if (!assetId) return;

    try {
      const res = await api.composerAssetArchive(assetId);
      if (res?.error) throw new Error(res.error);
      await loadAssets();
      try {
        toast({ type: 'success', message: 'Asset archived' });
      } catch {}
    } catch (e) {
      try {
        toast({ type: 'error', message: e?.message || 'Archive failed' });
      } catch {}
    }
  }, [loadAssets]);

  const restoreAsset = useCallback(async (asset) => {
    if (!asset) return;
    const assetId = asset.id || asset.assetId || asset._id;
    if (!assetId) return;

    try {
      const res = await api.composerAssetRestore(assetId);
      if (res?.error) throw new Error(res.error);
      await loadAssets();
      try {
        toast({ type: 'success', message: 'Asset restored' });
      } catch {}
    } catch (e) {
      try {
        toast({ type: 'error', message: e?.message || 'Restore failed' });
      } catch {}
    }
  }, [loadAssets]);

  const deleteArchivedAsset = useCallback(async (asset) => {
    if (!asset) return;
    const assetId = asset.id || asset.assetId || asset._id;
    if (!assetId) return;

    try {
      const res = await api.composerDeleteArchived(assetId);
      if (res?.error) throw new Error(res.error);
      await loadAssets();
      try {
        toast({ type: 'success', message: 'Archived asset deleted' });
      } catch {}
    } catch (e) {
      try {
        toast({ type: 'error', message: e?.message || 'Delete failed' });
      } catch {}
    }
  }, [loadAssets]);

  return {
    assets,
    archived,
    loading,
    pagination,
    loadAssets,
    archiveAsset,
    restoreAsset,
    deleteArchivedAsset,
    normalizeAsset
  };
}
